import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMutating } from '@tanstack/react-query';
import { MUTATION_KEY } from '../../api/releaseCheckoutSession';
import { Calendar } from '@/components/ui/calendar';
import { SearchFilters } from '../../routes/AvailabilityGuard';
import { useCalendarAvailability } from '../../api/availability/availability';
import { DateTime } from 'luxon';
import { applyGroupSizeFilter } from '../../helpers/applyGroupSizeFilter';
import { useSearchParams, useLocation } from 'react-router-dom';
import { DATESTR_FMT, MONTHSTR_FMT, isRestaurantClosed, TDR } from 'tdr-common';
import Spinner from '../Spinner';
import { fromLocalJsDate } from '../calendar/Calendar.utils';
import { HASH_ELEMENT_IDS } from '../../common/hashElementIds';
import { resolveHorizonDate } from '@/helpers/horizon';
import styles from './AvailabilityDatePicker.module.scss';
import { cn } from '@/lib/utils';
import { ChevronProps } from 'react-day-picker';
import ArrowLeft2 from 'iconsax-react/dist/esm/ArrowLeft2';
import ArrowRight2 from 'iconsax-react/dist/esm/ArrowRight2';
import { isMonthAvailable } from '@/helpers/getFirstAvailableDate';


type AvailabilityCalendarProps = {
	onChange?: (date: DateTime, availabilityPercent: number) => void;
	onDayClick?: (date: string, hash?: string) => void; // New callback for day clicks
	disabled?: boolean;
	filters: SearchFilters;
	entityWithPolicies?: any;
	setPastHorizonOpen?: (open: boolean) => void;
	tables: TDR.Table[]; // Required prop, no context fallback
	restaurant: TDR.Restaurant; // Required prop, no context fallback
	featureFlags?: TDR.FeatureFlags; // Optional feature flags
	setUnavailableDateOpen?: (open: boolean) => void;
	setUnavailableMonth?: (monthName: string) => void;
};

export const AvailabilityDatePicker = ({
	onChange,
	onDayClick,
	disabled,
	filters,
	entityWithPolicies,
	setPastHorizonOpen,
	tables,
	restaurant,
	featureFlags,
	setUnavailableDateOpen,
	setUnavailableMonth
}: AvailabilityCalendarProps) => {
	const { i18n } = useTranslation();
	const isReleasing = !!useIsMutating({
		mutationKey: [MUTATION_KEY],
		exact: true
	});
	const [searchParams] = useSearchParams();
	const location = useLocation();

	// Use props directly - no context needed
	const allTables = tables;

	const TODAY = DateTime.now().setZone(restaurant?.timezone);
	const [currentMonth, setCurrentMonth] = useState(filters.date ? DateTime.fromFormat(filters.date, DATESTR_FMT).toJSDate() : TODAY.toJSDate());
	const [startingMonth, setStartingMonth] = useState(undefined);

	// Apply group size filter to tables
	const filteredTables = applyGroupSizeFilter(allTables, filters.groupSize);
	const { data: selectedMonthAvailability, isLoading } = useCalendarAvailability(
		{ tables: filteredTables, month: DateTime.fromJSDate(currentMonth).toFormat(MONTHSTR_FMT), restaurantId: restaurant.id },
		isReleasing
	);

	const { date: horizon } = resolveHorizonDate({
		policies: entityWithPolicies?.availabilityRestrictingPolicies,
		restaurant,
		now: TODAY
	});
	const furthestBookableDate = horizon.toJSDate();

	const handleDateChange = (date: Date) => {
		if (!date) {
			return;
		}
		const luxonDate = fromLocalJsDate(date, restaurant?.timezone);
		const isClosed = isRestaurantClosed(luxonDate, restaurant);

		if (isClosed) {
			return onChange(luxonDate, 0);
		}

		const dateStr = luxonDate.toFormat(DATESTR_FMT);
		const dayAvailability = selectedMonthAvailability?.calendarAvailability[dateStr]?.available;
		if (dayAvailability === 0 || dayAvailability === undefined) {
			return onChange(luxonDate, 0);
		}

		return onChange(luxonDate, 100);
	};

	const handleDayClick = (date, modifiers) => {
		if (!modifiers.disabled && onDayClick) {
			const luxonDate = fromLocalJsDate(date, restaurant?.timezone);
			const dateStr = luxonDate.toFormat(DATESTR_FMT);

			// If clicking the same date, clear it; otherwise set the new date
			if (filters?.date && luxonDate.hasSame(DateTime.fromFormat(filters.date, DATESTR_FMT), 'day')) {
				onDayClick(undefined, undefined);
			}
			else {
				// Determine hash based on whether it's the same date already in URL
				const hash = dateStr === searchParams.get('date') ? location.hash : HASH_ELEMENT_IDS.timeslots;
				onDayClick(dateStr, hash);
			}
		}
	};

	const getDisabledDates = () => {
		const availableDates: Map<string, TDR.Availability.dayAvailability> = selectedMonthAvailability?.calendarAvailability;
		if (!availableDates) {
			return [];
		}
		const notAvailableDates: Date[] = [];
		const luxonDateToday = fromLocalJsDate(TODAY.toJSDate(), restaurant?.timezone);

		const isPastDate = luxonDateToday < TODAY.startOf('day');
		if (isPastDate) {
			notAvailableDates.push(TODAY.toJSDate());
		}

		const monthStart = DateTime.fromJSDate(currentMonth).startOf('month');
		const monthEnd = DateTime.fromJSDate(currentMonth).endOf('month');

		for (
			let day = monthStart;
			day <= monthEnd;
			day = day.plus({ days: 1 })
		) {
			const dateStr = day.toFormat(DATESTR_FMT);
			const jsDate = day.toJSDate();

			if (day < TODAY.startOf('day')) {
				continue;
			}

			const dayAvailability = availableDates[dateStr];

			if (!dayAvailability) {
				notAvailableDates.push(jsDate);
				continue;
			}

			const luxonDate = fromLocalJsDate(jsDate, restaurant?.timezone);
			if (
				dayAvailability.available === 0 ||
				isRestaurantClosed(luxonDate, restaurant) ||
				dayAvailability.staus === 'closed'
			) {
				notAvailableDates.push(jsDate);
			}
		}

		return notAvailableDates;
	};


	useEffect(() => {
		if (!selectedMonthAvailability?.calendarAvailability) {
			return;
		}
		if (startingMonth) {
			return;
		}
		if (currentMonth > furthestBookableDate) {
			setStartingMonth(TODAY.toJSDate());
			setCurrentMonth(TODAY.toJSDate());
			return;
		}
		const hasAvailableDate = isMonthAvailable(restaurant, currentMonth, selectedMonthAvailability?.calendarAvailability);

		if (!hasAvailableDate) {
			const nextMonth = DateTime.fromJSDate(currentMonth).plus({ months: 1 }).toJSDate();
			if (DateTime.fromJSDate(currentMonth).toFormat(MONTHSTR_FMT) !== DateTime.fromJSDate(nextMonth).toFormat(MONTHSTR_FMT)) {
				setCurrentMonth(nextMonth);
			}
		}
		else if (startingMonth === undefined) {
			setStartingMonth(currentMonth);
		}
	}, [selectedMonthAvailability, currentMonth, TODAY, restaurant]);

	// Jump to the URL date's month only when the date param changes (e.g. event Book Now).
	useEffect(() => {
		if (!filters?.date) {
			return;
		}
		const selectedDate = DateTime.fromFormat(filters.date, DATESTR_FMT);
		if (!selectedDate.isValid) {
			return;
		}
		setCurrentMonth(selectedDate.startOf('month').toJSDate());
	}, [filters?.date]);

	useEffect(() => {
		if (!filters?.date || !selectedMonthAvailability || isLoading) {
			return;
		}
		const selectedDate = DateTime.fromFormat(filters.date, DATESTR_FMT);
		if (!selectedDate.isValid) {
			return;
		}
		if (!selectedDate.hasSame(DateTime.fromJSDate(currentMonth), 'month')) {
			return;
		}

		// Wait for the displayed month's calendar data — avoids treating a June URL date
		// as unavailable while May's availability is still in memory (modal flash).
		const dateStr = selectedDate.toFormat(DATESTR_FMT);
		const cal = selectedMonthAvailability.calendarAvailability;
		const dayAvailability = cal instanceof Map ? cal.get(dateStr) : cal?.[dateStr];
		if (dayAvailability === undefined) {
			return;
		}

		handleDateChange(selectedDate.toJSDate());
	}, [selectedMonthAvailability, filters?.date, currentMonth, isLoading]);

	const customChevron = (props: ChevronProps) => {
		/* eslint-disable-next-line react/prop-types */
		const { orientation, disabled } = props;

		const handleClick = () => {
			if (orientation === 'right' && disabled && !featureFlags?.privateEvents) {
				setPastHorizonOpen?.(true);
			}
			else {
				if (orientation === 'left' && disabled) {
					setUnavailableDateOpen?.(true);
					const prevMonth = DateTime.fromJSDate(currentMonth).minus({ months: 1 });
					setUnavailableMonth?.(prevMonth.toFormat('MMMM'));
				}
			}
		};

		return (
			<div className="w-8 h-8 flex justify-center items-center rounded-2xl hover:bg-tablz-med-gray-6" onClick={handleClick}>
				{orientation === 'left' ?
					<ArrowLeft2 size={20} className="!w-5 !h-5" /> :
					<ArrowRight2 size={20} className="!w-5 !h-5" />
				}
			</div>
		);
	};

	return (
		<div className={disabled ? 'pointer-events-none opacity-40 w-full' : ''}>
			{filters?.groupSize && (isLoading || !!isReleasing) && (
				<div className="absolute right-[70px]" data-cy="calendarLoading">
					<Spinner size="xs" />
				</div>
			)}
			<Calendar
				disableNavigation={!furthestBookableDate}
				mode='single'
				month={currentMonth}
				onMonthChange={setCurrentMonth}
				selected={filters.date ? DateTime.fromFormat(filters.date, DATESTR_FMT).toJSDate() : null}
				onSelect={handleDateChange}
				classNames={{
					root: `w-full !p-0 ${!furthestBookableDate ? 'pointer-events-none opacity-40' : ''}`,
					day: 'bg-tablz-med-gray-2 border border-tablz-med-gray-4 !rounded-2xl shadow-xs flex items-center justify-center w-full aspect-sqaure hover:bg-tablz-med-gray-6',
					day_button: 'text-tablz-text-white-1 font-poppins font-semibold text-base !ring-0',
					week: 'grid grid-cols-7 mt-0 gap-2',
					weeks: 'flex flex-col gap-2',
					month: 'gap-0',
					nav: 'top-3',
					month_caption: 'h-fit text-base',
					month_grid: 'w-full',
					chevron: '!w-5 !h-5',
					caption_label: 'text-tablz-text-white-2 font-poppins text-base font-semibold my-4 capitalize',
					weekdays: 'my-3 flex grid grid-cols-7 gap-2',
					weekday: 'w-full text-center text-tablz-text-gray-1 font-poppins text-base font-medium',
					selected: '!rounded-2xl border-2 !border-tablz-dark-gray-1 bg-tablz-med-gray-6 shadow-selected',
					disabled: cn('border-tablz-med-gray-1 bg-tablz-med-gray-5 shadow-xs !cursor-default hover:bg-tablz-med-gray-5 [&>*]:cursor-default', styles.disabledDayLine)
				}}
				onDayClick={handleDayClick}
				showOutsideDays={false}
				components={{
					Chevron: customChevron
				}}
				startMonth={TODAY.toJSDate()}
				endMonth={
					featureFlags?.privateEvents
						? undefined
						: (furthestBookableDate ? new Date(furthestBookableDate) : TODAY.toJSDate())
				}
				disabled={[
					{ before: TODAY.toJSDate(), after: !furthestBookableDate ? TODAY.toJSDate() : (disabled ? TODAY.minus({ days: 1 }).toJSDate() : furthestBookableDate) },
					...getDisabledDates()
				]}
				formatters={{
					formatWeekdayName: (date) => date.toLocaleString(i18n.language === 'fr' ? 'fr' : 'en', { weekday: 'short' }).charAt(0).toUpperCase(),
					formatCaption: (date) => date.toLocaleString(i18n.language === 'fr' ? 'fr' : 'en', { month: 'long', year: 'numeric' })
				}}
			/>
		</div>
	);
};
